import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { Folder, Plus } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { nextgenConversationPath } from "~/pages/manifold-nextgen/paths";
import { Agent, AgentStatus } from "~/services/agents";
import { createProjectConversation, projectConversationsPath } from "~/services/projects";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

interface AgentOptionProps {
  agent: Agent;
  selected: boolean;
  onSelect: () => void;
}

const AgentOption = ({ agent, selected, onSelect }: AgentOptionProps) => (
  <li className="h-full">
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-transparent px-2 py-3",
        "ring-1 ring-border/60 transition-all",
        "hover:bg-accent/40 hover:ring-border",
        selected && "bg-accent/40 ring-2 ring-primary hover:ring-primary"
      )}
    >
      <AgentAvatar id={agent.id} name={agent.name} size={48} />
      <p className="w-full truncate text-center text-xs font-medium text-foreground">
        {agent.display_name}
      </p>
    </button>
  </li>
);

interface DatasetOptionProps {
  dataset: Dataset;
  selected: boolean;
  onSelect: () => void;
}

const DatasetOption = ({ dataset, selected, onSelect }: DatasetOptionProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-transparent px-3 py-2",
        "ring-1 ring-border/60 transition-all text-left",
        "hover:bg-accent/40 hover:ring-border",
        selected && "bg-accent/40 ring-2 ring-primary hover:ring-primary"
      )}
    >
      <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="line-clamp-1 text-xs font-medium text-foreground">{dataset.title}</p>
    </button>
  </li>
);

export const NewSessionPicker = () => {
  const { projectId, attachedAgents, attachedDatasets } = useProjectContext();
  const navigate = useNavigate();
  const { showError } = useToast();

  const visibleAgents = (attachedAgents ?? []).filter((a) => a.status !== AgentStatus.DISABLED);

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [datasetsDialogOpen, setDatasetsDialogOpen] = useState(false);

  useEffect(() => {
    if (attachedAgents !== undefined) {
      setSelectedAgentId(visibleAgents.length === 1 ? visibleAgents[0].id : null);
    }
    if (attachedDatasets !== undefined) {
      setSelectedDatasetId(attachedDatasets.length === 1 ? attachedDatasets[0].id : null);
    }
  }, [attachedAgents, attachedDatasets]);

  const canStart =
    visibleAgents.length > 0 &&
    (attachedDatasets?.length ?? 0) > 0 &&
    !!selectedAgentId &&
    !!selectedDatasetId;

  const handleStart = async () => {
    if (!selectedAgentId || !selectedDatasetId) {
      return;
    }
    setIsPending(true);
    try {
      const conversation = await createProjectConversation(projectId, {
        agent_id: selectedAgentId,
        dataset_id: selectedDatasetId,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await mutate(projectConversationsPath(projectId));
      navigate(nextgenConversationPath(projectId, conversation.id));
    } catch {
      showError("Failed to start session. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-6">
          <div>
            <h1 className="text-xl font-semibold">New Session</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Select an agent and a dataset to get started.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Agents
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-0.5 text-xs text-primary hover:text-primary/80"
                onClick={() => setAgentsDialogOpen(true)}
              >
                <Plus className="size-3.5" />
                Add Agents
              </Button>
            </div>
            {visibleAgents.length === 0 ? (
              <div className="flex items-center justify-center rounded-lg border border-dashed border-border px-4 py-6">
                <p className="text-xs text-muted-foreground">No agents attached yet.</p>
              </div>
            ) : (
              <ul className="grid grid-cols-3 gap-2" role="list">
                {visibleAgents.map((agent) => (
                  <AgentOption
                    key={agent.id}
                    agent={agent}
                    selected={selectedAgentId === agent.id}
                    onSelect={() => setSelectedAgentId(agent.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Datasets
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-0.5 text-xs text-primary hover:text-primary/80"
                onClick={() => setDatasetsDialogOpen(true)}
              >
                <Plus className="size-3.5" />
                Add Datasets
              </Button>
            </div>
            {(attachedDatasets ?? []).length === 0 ? (
              <div className="flex items-center justify-center rounded-lg border border-dashed border-border px-4 py-6">
                <p className="text-xs text-muted-foreground">No datasets attached yet.</p>
              </div>
            ) : (
              <ul className="flex flex-col gap-1" role="list">
                {(attachedDatasets ?? []).map((dataset) => (
                  <DatasetOption
                    key={dataset.id}
                    dataset={dataset}
                    selected={selectedDatasetId === dataset.id}
                    onSelect={() => setSelectedDatasetId(dataset.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          <Button onClick={handleStart} disabled={!canStart || isPending} className="self-end">
            {isPending ? "Starting…" : "Start"}
          </Button>
        </div>
      </div>

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsDialogOpen}
        onOpenChange={setAgentsDialogOpen}
        attached={attachedAgents ?? []}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsDialogOpen}
        onOpenChange={setDatasetsDialogOpen}
        attachedIds={attachedDatasets?.map((d) => d.id) ?? []}
      />
    </>
  );
};
